import { Link } from "react-router-dom";
import { Gavel, Target, ClipboardCheck, TrendingUp } from "lucide-react";

/**
 * Static About page. Positions Boardroom as a personal accountability OS —
 * board meetings you hold with yourself (and, optionally, a partner) so the
 * week doesn't slip by unexamined. Marketing tone: direct, no fluff.
 */
export default function About() {
  return (
    <div className="max-w-3xl mx-auto px-6 pb-24" data-testid="about-page">
      <p className="label-eyebrow mt-2 text-[#c89f45]">ABOUT · SESSION INTEGRITY</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
        Boardroom is the meeting you owe yourself.
      </h1>
      <p className="text-[#bbb] text-base sm:text-lg mt-6 leading-relaxed">
        Most people run a business, a life, a project — without ever sitting
        down at their own boardroom table. Weeks blur. Wins go unlogged. Losses
        never get a hearing. Goals drift because nobody called them to account.
      </p>
      <p className="text-[#bbb] text-base sm:text-lg mt-4 leading-relaxed">
        Boardroom fixes that with a simple, ruthless rhythm: a{" "}
        <span className="text-[#fafafa]">weekly stand-up</span> to name your
        wins, losses and next moves, and a{" "}
        <span className="text-[#fafafa]">monthly deep-dive</span> to zoom out on
        finances, metrics, habits and strategy. Both are recorded. Both produce
        AI-generated minutes. Both leave a paper trail so future-you can hold
        past-you accountable.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-12">
        <FeatureCard
          Icon={Gavel}
          title="Call session to order"
          body="Record audio or full video. Structured agenda: wins, losses, goals, challenges, action items. No blank-page paralysis."
        />
        <FeatureCard
          Icon={ClipboardCheck}
          title="AI-generated minutes"
          body="Walk out with a signed executive summary, a synthesized action plan and next-focus commitments. Every session, on file."
        />
        <FeatureCard
          Icon={Target}
          title="Action items with sign-offs"
          body="Every commitment becomes a task. Sign it off with a date. Momentum becomes measurable — weekly and monthly rollups."
        />
        <FeatureCard
          Icon={TrendingUp}
          title="Monthly board review"
          body="Once a month, Boardroom rolls up the last four weekly sessions into a proper strategic review — financials, metrics, habits, big-picture."
        />
      </div>

      <div className="mt-14 border-t border-[#1a1a1a] pt-8">
        <h2 className="font-serif text-2xl">Who it's for</h2>
        <ul className="mt-4 space-y-2 text-[#bbb]">
          <li>· Founders, indie builders, and operators running their own show</li>
          <li>· Accountability partners who want a shared, structured cadence</li>
          <li>· Anyone who's tired of goals dying in a Notion doc nobody re-reads</li>
        </ul>
      </div>

      <div className="mt-14 flex flex-wrap gap-3">
        <Link
          to="/meetings/new"
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-5 py-2.5 transition-colors"
          data-testid="about-cta-new"
        >
          Call your first session
        </Link>
        <Link
          to="/faq"
          className="border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-5 py-2.5 transition-colors"
          data-testid="about-cta-faq"
        >
          Read the FAQ
        </Link>
      </div>
    </div>
  );
}

function FeatureCard({ Icon, title, body }) {
  return (
    <div className="surface rounded-md p-5">
      <Icon size={18} className="text-[#c89f45]" />
      <p className="font-serif text-lg mt-3">{title}</p>
      <p className="text-sm text-[#888] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
